import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { eatDayOf, formatIsoDay } from '@wodi/domain';
import type { PatientSearchResult } from '@wodi/shared';
import {
  Button,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { mortuaryBoardQuery, mortuaryFormOptionsQuery, receiveBody } from '../api/mortuary.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientResultRow } from '../patients/patient-result-row.tsx';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import styles from '../vital-events/vital-events.module.css';
import { formatInstant } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import {
  eatClockNow,
  eatInstant,
  errorText,
  PLACE_WORDS,
  validTime,
} from '../vital-events/words.ts';

// Receive a body into the mortuary (docs/plans/phase2-mortuary-and-notifications.md §6): from
// a death recorded here, or brought in from outside (a registered patient, or a person nobody
// can name yet, registered with a temporary label). The death and the body's own visit are
// written with the case.

const route = getRouteApi('/_app/mortuary/receive');

const NO_SLOT = 'none';
const UNKNOWN_SEX = 'not-known';

export function ReceiveBodyPage() {
  const search = route.useSearch();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const board = useQuery(mortuaryBoardQuery);
  const options = useQuery(mortuaryFormOptionsQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [source, setSource] = useState<'facility_death' | 'brought_in'>(
    search.deathId === undefined ? 'brought_in' : 'facility_death',
  );
  const [deathId, setDeathId] = useState<string | null>(search.deathId ?? null);
  const [who, setWho] = useState<'registered' | 'unknown'>('registered');
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [text, setText] = useState('');
  const [sex, setSex] = useState(UNKNOWN_SEX);
  const [ageYears, setAgeYears] = useState('');
  const [description, setDescription] = useState('');
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(new Date())));
  const [time, setTime] = useState(() => eatClockNow());
  const [slotId, setSlotId] = useState<string>(NO_SLOT);
  const [broughtBy, setBroughtBy] = useState('');
  const [notes, setNotes] = useState('');
  const mayIntake = session !== null && can(session, 'mortuary.intake');
  const age = Number(ageYears);
  const ageOk = ageYears === '' || (Number.isInteger(age) && age >= 0 && age <= 130);

  const sexes = options.data?.sexes ?? [];
  const chosenSex = sexes.find((s) => `${s.system}|${s.code}` === sex) ?? null;
  const freeSlots = (board.data?.coldRooms ?? []).flatMap((r) =>
    r.slots
      .filter((s) => s.state === 'free')
      .map((s) => ({ id: s.id, label: `${r.name}, ${s.label}` })),
  );
  const waiting = board.data?.awaitingIntake ?? [];
  const common = {
    clientRequestId,
    slotId: slotId === NO_SLOT ? null : slotId,
    broughtBy: broughtBy.trim() === '' ? null : broughtBy.trim(),
    intakeNotes: notes.trim() === '' ? null : notes.trim(),
  };
  const receive = useMutation({
    mutationFn: () =>
      receiveBody(
        source === 'facility_death'
          ? { source, deathRecordId: deathId ?? '', ...common }
          : {
              source,
              diedAt: eatInstant(day ?? '', time),
              person:
                who === 'registered'
                  ? { kind: 'registered', patientId: patient?.id ?? '' }
                  : {
                      kind: 'unknown',
                      sex:
                        chosenSex === null
                          ? null
                          : { system: chosenSex.system, code: chosenSex.code },
                      birth:
                        ageYears === ''
                          ? null
                          : { kind: 'estimated', age: { unit: 'years', value: age } },
                      description: description.trim() === '' ? null : description.trim(),
                    },
              ...common,
            },
      ),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ['mortuary'] });
      await queryClient.invalidateQueries({ queryKey: ['deaths'] });
      toasts.show({ title: 'Body received', description: c.caseNumber });
      await navigate({ to: '/mortuary/cases/$caseId', params: { caseId: c.id } });
    },
  });
  const ready =
    online &&
    mayIntake &&
    !receive.isPending &&
    (source === 'facility_death'
      ? deathId !== null
      : day !== null && validTime(time) && (who === 'registered' ? patient !== null : ageOk));

  if (!mayIntake) {
    return (
      <>
        <PageHeader title="Receive body" />
        <Notice tone="info">
          You don&apos;t have permission to receive bodies. Ask the mortuary attendant.
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Receive body"
        description="The body gets a case number and its own mortuary visit for the storage charges."
      />
      {board.isPending ? <LoadingBlock label="Loading the mortuary" /> : null}
      {board.isError ? (
        <Notice tone="error">{errorText(board.error, 'The mortuary could not be loaded.')}</Notice>
      ) : null}
      <Section title="Body">
        <form
          className={styles.form}
          aria-label="Receive body"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) receive.mutate();
          }}
        >
          <RadioGroup
            label="Where the death happened"
            orientation="horizontal"
            value={source}
            onChange={(v) => {
              setSource(v === 'facility_death' ? 'facility_death' : 'brought_in');
            }}
            options={[
              { id: 'facility_death', label: 'Died here' },
              { id: 'brought_in', label: 'Brought in from outside' },
            ]}
          />
          {source === 'facility_death' ? (
            waiting.length === 0 && deathId === null ? (
              <Notice tone="info">
                No deaths here are waiting to be received. Record the death on the patient&apos;s
                stay or casualty visit first.
              </Notice>
            ) : (
              <RadioGroup
                label="Death recorded here"
                value={deathId ?? ''}
                onChange={setDeathId}
                options={waiting.map((d) => ({
                  id: d.deathId,
                  label: `${d.patient.displayName} (${d.patient.patientNumber})`,
                  description: `${PLACE_WORDS[d.place]}, ${formatInstant(d.diedAt)}`,
                }))}
              />
            )
          ) : (
            <>
              <RadioGroup
                label="Who"
                orientation="horizontal"
                value={who}
                onChange={(v) => {
                  setWho(v === 'unknown' ? 'unknown' : 'registered');
                }}
                options={[
                  { id: 'registered', label: 'A registered patient' },
                  { id: 'unknown', label: 'Not identified' },
                ]}
              />
              {who === 'registered' ? (
                patient === null ? (
                  <PatientSearchPicker
                    inputValue={text}
                    onInputChange={setText}
                    onSelect={setPatient}
                    emptyAction={() => (
                      <span className={styles.meta}>
                        Not found? Register the person at reception, or choose Not identified.
                      </span>
                    )}
                  />
                ) : (
                  <div className={styles.actions}>
                    <PatientResultRow result={patient} />
                    <Button
                      variant="quiet"
                      onPress={() => {
                        setPatient(null);
                        setText('');
                      }}
                    >
                      Change
                    </Button>
                  </div>
                )
              ) : (
                <>
                  <RadioGroup
                    label="Sex"
                    orientation="horizontal"
                    value={sex}
                    onChange={setSex}
                    options={[
                      { id: UNKNOWN_SEX, label: 'Not known' },
                      ...sexes.map((s) => ({ id: `${s.system}|${s.code}`, label: s.display })),
                    ]}
                  />
                  <TextField
                    label="Estimated age (optional)"
                    unit="years"
                    inputMode="numeric"
                    value={ageYears}
                    onChange={setAgeYears}
                    width="sm"
                    {...(ageOk ? {} : { errorMessage: 'Whole years from 0 to 130.' })}
                  />
                  <TextArea
                    label="Description (optional)"
                    description="Clothing, marks, where found. Shown on patient screens only."
                    value={description}
                    onChange={setDescription}
                    rows={2}
                  />
                </>
              )}
              <div className={styles.row}>
                <DateField label="Date of death" value={day} onChange={setDay} />
                <TextField
                  label="Time of death"
                  description="As reported; 24-hour, like 14:05"
                  value={time}
                  onChange={setTime}
                  width="sm"
                  {...(time !== '' && !validTime(time)
                    ? { errorMessage: 'Write the time as hours and minutes, like 14:05.' }
                    : {})}
                />
              </div>
            </>
          )}
          <Select
            label="Slot"
            options={[{ id: NO_SLOT, label: 'No slot yet' }, ...freeSlots]}
            value={slotId}
            onChange={(v) => {
              setSlotId(typeof v === 'string' ? v : NO_SLOT);
            }}
          />
          <TextField label="Brought by (optional)" value={broughtBy} onChange={setBroughtBy} />
          <TextArea
            label="Intake notes (optional)"
            description="Marks, belongings, condition."
            value={notes}
            onChange={setNotes}
            rows={2}
          />
          {!online ? (
            <Notice tone="info">
              No connection to Wodi. Receiving a body waits for the connection.
            </Notice>
          ) : null}
          {receive.error === null ? null : (
            <Notice tone="error">
              {errorText(receive.error, "The body wasn't received. Try again.")}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!ready}
              isPending={receive.isPending}
              pendingLabel="Receiving…"
            >
              Receive body
            </Button>
          </div>
        </form>
      </Section>
    </div>
  );
}
